/**
 * LoginForm — react-hook-form + zod, calls authApi.login on submit.
 *
 * Server error mapping: gateway returns
 *   { error: '<code>', message: '<human>' }
 * We translate `error` into an i18n key so users get a Russian
 * message regardless of the server's English fallback.
 */
import { type LoginRequest, loginRequestSchema } from '@chaos/types/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ApiError, authApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { useToaster } from '../ui/toast';

interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toaster = useToaster();
  const setSession = useAuthStore((s) => s.setSession);

  const form = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  const login = useMutation({
    mutationFn: authApi.login,
    onSuccess: async (resp) => {
      // Hydrate the store with the live /me view (gives us the
      // canonical workspaces array).
      try {
        const me = await authApi.me();
        setSession(me);
      } catch {
        // Fall back to the minimal session shape from login response.
        setSession({
          user: resp.user,
          workspaces: [
            {
              workspace: resp.workspace,
              membership: resp.membership,
            },
          ],
        });
      }
      onSuccess?.();
      void navigate({ to: '/dashboard' });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const codeKey = `auth.errors.${err.code}`;
        const message = t(codeKey, t('auth.errors.unknown'));
        form.setError('root', { message });
        toaster.toast({ variant: 'destructive', title: message });
      } else {
        const message = t('auth.errors.network');
        form.setError('root', { message });
        toaster.toast({ variant: 'destructive', title: message });
      }
    },
  });

  const onSubmit = (values: LoginRequest) => {
    void login.mutate(values);
  };

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('auth.login.email_label')}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.login.email_placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('auth.login.password_label')}</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('auth.login.password_placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {login.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>
    </Form>
  );
}
